/**
 * Step 3: Review & Validate
 *
 * Employee list with validation integration.
 * - Displays employees in editable table
 * - Runs validation against backend
 * - Shows validation dashboard with errors/warnings
 * - Supports add/edit/remove employee operations
 *
 * Session 4.2: Step 3 - Map & Validate
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { WizardNavigation } from '../WizardNavigation';
import { EmployeeTable } from '../EmployeeTable';
import { EmployeeEditModal } from '../EmployeeEditModal';
import { useWizard } from '../../context/WizardContext';
import { ValidationDashboard } from '@/components/nib/validation';
import { useValidateSubmission } from '@/hooks/useValidation';
import { useBusinessProfile } from '@/hooks/business/useBusinessProfile';
import type { WizardEmployee } from '../../context/types';
import type {
  EmployeeValidationResult,
  EmployeeValidationData,
} from '@/types/validation';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Convert wizard month/year to MMYY format for API.
 */
function formatPeriod(month: number, year: number): string {
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}${yy}`;
}

/**
 * Convert wizard employee to validation API format.
 */
function toValidationEmployee(emp: WizardEmployee): EmployeeValidationData {
  return {
    employee_ni_number: emp.employeeNi,
    last_name: emp.lastName,
    first_name: emp.firstName,
    middle_initial: emp.middleInitial,
    original_gross_wages: emp.basicWages,
    original_gratuity: emp.gratuityWages,
    weeks_worked: emp.weeksWorked,
    category: emp.category,
    pay_indicator: emp.payIndicator,
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Step 3: Review and validate employees.
 */
export function Step3Validate() {
  const { state, nextStep, updateEmployee, removeEmployee } = useWizard();
  const { data: businessProfile } = useBusinessProfile();

  // Validation mutation
  const {
    mutate: validate,
    data: validationResult,
    isPending: isValidating,
  } = useValidateSubmission();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingEmployee, setEditingEmployee] = useState<WizardEmployee | null>(
    null
  );

  // Create validation map keyed by employee NI
  const validationByNi = useMemo(() => {
    if (!validationResult) return undefined;

    const map = new Map<string, EmployeeValidationResult>();
    validationResult.employee_results.forEach(result => {
      map.set(result.employee_ni, result);
    });
    return map;
  }, [validationResult]);

  // Run validation when employees change
  useEffect(() => {
    if (
      state.employees.length === 0 ||
      !businessProfile?.employer_ni_number ||
      !state.contributionMonth ||
      !state.contributionYear
    ) {
      return;
    }

    const period = formatPeriod(
      state.contributionMonth,
      state.contributionYear
    );

    validate({
      employer_ni: businessProfile.employer_ni_number,
      contribution_period: period,
      format_type:
        state.formatType === 'HOSPITALITY' ? 'HOSPITALITY' : 'NON_HOSPITALITY',
      employees: state.employees.map(toValidationEmployee),
    });
  }, [
    state.employees,
    state.contributionMonth,
    state.contributionYear,
    state.formatType,
    validate,
    businessProfile,
  ]);

  // Handler: Open edit modal
  const handleEditEmployee = useCallback((employee: WizardEmployee) => {
    setEditingEmployee(employee);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  // Handler: Open add modal
  const handleAddEmployee = useCallback(() => {
    setEditingEmployee(null);
    setModalMode('add');
    setModalOpen(true);
  }, []);

  // Handler: Close modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingEmployee(null);
  }, []);

  // Handler: Apply suggestion from validation (find employee by NI)
  const handleApplySuggestion = useCallback(
    (employeeNi: string, field: string, value: string) => {
      const employee = state.employees.find(e => e.employeeNi === employeeNi);
      if (!employee) return;

      // Map field to wizard employee property
      const fieldMap: Record<string, keyof WizardEmployee> = {
        employee_ni_number: 'employeeNi',
        last_name: 'lastName',
        first_name: 'firstName',
        middle_initial: 'middleInitial',
      };

      const wizardField = fieldMap[field];
      if (wizardField) {
        updateEmployee(employee.id, { [wizardField]: value });
      }
    },
    [state.employees, updateEmployee]
  );

  // Handler: Edit employee from validation (find by NI)
  const handleEditFromValidation = useCallback(
    (employeeNi: string) => {
      const employee = state.employees.find(e => e.employeeNi === employeeNi);
      if (employee) {
        handleEditEmployee(employee);
      }
    },
    [state.employees, handleEditEmployee]
  );

  // Handler: Remove employee from validation (find by NI)
  const handleRemoveFromValidation = useCallback(
    (employeeNi: string) => {
      const employee = state.employees.find(e => e.employeeNi === employeeNi);
      if (employee) {
        removeEmployee(employee.id);
      }
    },
    [state.employees, removeEmployee]
  );

  // Handler: Revalidate
  const handleRevalidate = useCallback(() => {
    if (
      state.employees.length === 0 ||
      !businessProfile?.employer_ni_number ||
      !state.contributionMonth ||
      !state.contributionYear
    ) {
      return;
    }

    const period = formatPeriod(
      state.contributionMonth,
      state.contributionYear
    );

    validate({
      employer_ni: businessProfile.employer_ni_number,
      contribution_period: period,
      format_type:
        state.formatType === 'HOSPITALITY' ? 'HOSPITALITY' : 'NON_HOSPITALITY',
      employees: state.employees.map(toValidationEmployee),
    });
  }, [
    state.employees,
    state.contributionMonth,
    state.contributionYear,
    state.formatType,
    validate,
    businessProfile,
  ]);

  // Check if can proceed (no errors)
  const canProceed = useMemo(() => {
    if (state.employees.length === 0) return false;
    if (!validationResult) return false;
    return validationResult.is_valid;
  }, [state.employees.length, validationResult]);

  // Employee counts
  const employeeCount = state.employees.length;
  const errorCount = validationResult?.summary.total_errors ?? 0;
  const warningCount = validationResult?.summary.total_warnings ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Review & Validate Employees
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {employeeCount} employee{employeeCount !== 1 ? 's' : ''} loaded
            {errorCount > 0 && (
              <span className="text-red-600 ml-2">
                • {errorCount} error{errorCount !== 1 ? 's' : ''}
              </span>
            )}
            {warningCount > 0 && (
              <span className="text-amber-600 ml-2">
                • {warningCount} warning{warningCount !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddEmployee}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Employee
        </button>
      </div>

      {/* Employee Table */}
      <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
        <EmployeeTable
          validationByNi={validationByNi}
          onEditEmployee={handleEditEmployee}
        />
      </div>

      {/* Validation Dashboard */}
      {state.employees.length > 0 && (
        <div className="mb-6">
          <ValidationDashboard
            result={validationResult ?? null}
            isValidating={isValidating}
            onRevalidate={handleRevalidate}
            onApplySuggestion={handleApplySuggestion}
            onEditEmployee={handleEditFromValidation}
            onRemoveEmployee={handleRemoveFromValidation}
          />
        </div>
      )}

      {/* Empty State */}
      {state.employees.length === 0 && (
        <div className="mb-6 p-8 text-center border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-sm text-gray-500">
            No employees yet. Add employees to continue.
          </p>
          <button
            type="button"
            onClick={handleAddEmployee}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            <PlusIcon className="h-4 w-4" />
            Add First Employee
          </button>
        </div>
      )}

      {/* Navigation */}
      <WizardNavigation onNext={nextStep} isNextDisabled={!canProceed} />

      {/* Edit/Add Modal */}
      <EmployeeEditModal
        employee={editingEmployee}
        isOpen={modalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
      />
    </div>
  );
}
